import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import {Bell, ZoomIn, X, ChevronLeft, ChevronRight} from 'lucide-react';
import { createPortal } from 'react-dom';
import 'swiper/css';
import './FloatingNotification.css';
import { API_BASE } from '../config/api';
// Remove ImagePreviewModal import as we're creating our own

// Create a new NotificationImageModal component for full screen display
const NotificationImageModal = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-50 p-2 bg-black bg-opacity-50 rounded-full"
        aria-label="Close modal"
      >
        <X size={28} strokeWidth={2} />
      </button>
      
      <img 
        src={imageUrl} 
        alt="Notification image" 
        className="w-screen h-screen object-contain"
      />
    </div>,
    document.body
  );
};

// Slides loop, so slide 0 sits next to the last one. Plain subtraction would treat
// them as the furthest apart and drop the image right where the user is looking.
const ringDistance = (i, active, n) => {
  const d = Math.abs(i - active);
  return Math.min(d, n - d);
};

const FloatingNotification = ({ notifications = [], onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [swiper, setSwiper] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Reset visibility when new notifications arrive
    setIsVisible(true);
    setActive(0);
  }, [notifications]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose(); // This will now set lastClosedTime in the context
  };

  useEffect(() => {
    if (isVisible && notifications.length > 0) {
      // Auto-hide after 30 minutes of being visible
      const timer = setTimeout(() => {
        handleClose();
      }, 1800000); // 30 minutes in milliseconds

      return () => clearTimeout(timer);
    }
  }, [isVisible, notifications]);

  // One slide per notification, each carrying its own date. This used to be two
  // nested carousels (days on the outside, items within a day inside), which left
  // nothing sensible for a prev/next button to drive — the arrows would step days
  // while an inner autoplay moved items on its own. A flat list makes "3/7" and the
  // arrows mean exactly one thing.
  const items = notifications.map((n) => ({
    ...n,
    date: n.created_at
      ? new Date(n.created_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        })
      : 'Hôm nay',
  }));
  const many = items.length > 1;

  return (
    <AnimatePresence>
      {isVisible && items.length > 0 && (
        // z-[1000], not z-50: the floating pills on the right are z-[999] and sit
        // exactly where the "next" arrow lands, which buried it.
        // No -translate-x-1/2 here: that is the leftover half of the `left-1/2`
        // centering idiom, and paired with `right-3` it dragged the box a further
        // half-width to the left every time it got wider.
        <motion.div
          initial={{ y: -100, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -100, opacity: 0, scale: 0.95 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25,
            mass: 0.5
          }}
          className="fixed top-40 right-3 z-[1000] pointer-events-auto w-full max-w-[90vw] sm:max-w-2xl px-4"
        >
          <div className="bg-white rounded-xl shadow-xl border border-[#0096b1]/25 min-w-[300px] max-w-2xl overflow-hidden">

            {/* Thanh tiêu đề: ngày, vị trí trong danh sách, nút đóng */}
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#0096b1]/5 border-b border-[#0096b1]/15">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#2b5356] min-w-0">
                <Bell className="shrink-0" size={14} strokeWidth={3} />
                <span className="truncate">Thông báo ngày: {items[active]?.date}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {many && (
                  <span className="text-[11px] font-semibold tabular-nums text-gray-500">
                    {active + 1}/{items.length}
                  </span>
                )}
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-full p-1 hover:bg-gray-100 focus:outline-none"
                  aria-label="Đóng thông báo"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            <div className="relative">
              <Swiper
                modules={[Autoplay]}
                spaceBetween={24}
                slidesPerView={1}
                autoHeight
                autoplay={{
                  delay: 8000,
                  // Once someone takes the arrows the carousel stops moving under
                  // them — they are reading, not browsing.
                  disableOnInteraction: true,
                  pauseOnMouseEnter: true,
                }}
                loop={many}
                onSwiper={setSwiper}
                onSlideChange={(sw) => setActive(sw.realIndex)}
                className="notification-swiper"
              >
                {items.map((notification, index) => (
                  <SwiperSlide key={`notification-${index}`}>
                    <div className={many ? 'px-8 py-3' : 'px-4 py-3'}>
                      {/* Every notification carries a full-size image — 135 of them, some
                          over 1 MB, and a decoded bitmap costs several times its file size.
                          Swiper keeps all slides mounted, so mounting all the <img> tags
                          walked straight into iOS Safari's per-tab memory ceiling: swipe
                          far enough and the tab is killed ("Đã có sự cố xảy ra liên tục").
                          Only the slide being read and its immediate neighbours load. */}
                      <NotificationItem
                        notification={notification}
                        showImage={ringDistance(index, active, items.length) <= 1}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {many && (
                <>
                  <button
                    type="button"
                    onClick={() => swiper?.slidePrev()}
                    aria-label="Thông báo trước"
                    className="absolute left-1 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-7 h-7 rounded-full bg-white/95 text-[#0096b1] shadow ring-1 ring-black/5 hover:bg-[#0096b1] hover:text-white transition-colors"
                  >
                    <ChevronLeft size={18} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => swiper?.slideNext()}
                    aria-label="Thông báo sau"
                    className="absolute right-1 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-7 h-7 rounded-full bg-white/95 text-[#0096b1] shadow ring-1 ring-black/5 hover:bg-[#0096b1] hover:text-white transition-colors"
                  >
                    <ChevronRight size={18} strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Extracted notification item component
const NotificationItem = ({ notification, showImage = true }) => {
  const { content, type = 'announcement', image_url } = notification;
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const handleImageClick = (e) => {
    e.stopPropagation(); // Stop event propagation
    setIsImageModalOpen(true);
  };

  return (
    <div className="flex flex-col">
      {/* Announcements are authored with real line breaks ("Part 2: ...\nPassage 1: ...");
          a plain <p> collapsed them into one run-on paragraph. Left-aligned because
          centring a six-line list is what made it unreadable. */}
      <p className={`font-medium whitespace-pre-line break-words text-left leading-relaxed max-h-[40vh] overflow-y-auto
        ${type === 'update' ? 'text-green-700' : ''}
        ${type === 'announcement' ? 'text-blue-700' : ''}
        ${type === 'maintenance' ? 'text-orange-700' : ''}
      `}>{content}</p>
      {image_url && showImage && (
        <div
          className="relative cursor-pointer mt-3"
          onClick={handleImageClick}
        >
          <img
            src={`${API_BASE}${image_url}`}
            alt="Notification image"
            loading="lazy"
            decoding="async"
            className="rounded-lg w-full max-w-[600px] max-h-[50vh] object-contain mx-auto transition-transform duration-300 hover:scale-105"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg">
            <ZoomIn className="text-white absolute top-2 right-2 bg-black bg-opacity-40 p-1 rounded-full" size={24} strokeWidth={3}/>
          </div>
        </div>
      )}

      {/* Use our new NotificationImageModal for full screen display */}
      <NotificationImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={image_url ? `${API_BASE}${image_url}` : ''}
      />
    </div>
  );
};

export default FloatingNotification;
